import { Stack, Typography, useTheme } from "@mui/material";
import { Banknote, Home } from "lucide-react";
import type { FC } from "react";
import { Trans } from "react-i18next";
import {
	getResourcePath,
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { formatCurrency } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { ComparisonColumn } from "web_myplann/Inspiration/Components/ComparisonColumn";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import { INSPIRATION_CURRENCY } from "web_myplann/Inspiration/constants";
import {
	formatMillionRange,
	OWN_HOME_HORIZON_YEARS,
	type OwnHomeContext,
} from "web_myplann/Inspiration/Utils/ownHomeMath";

const R = Resources.Inspiration.OwnHome.Result;

type Props = {
	context: OwnHomeContext;
};

export const RentVsOwnComparison: FC<Props> = ({ context }) => {
	const { t } = useResource();
	const theme = useTheme();

	const {
		rent,
		savings,
		totalRent,
		priceLow,
		priceHigh,
		propertyLow,
		propertyHigh,
		state,
	} = context;

	return (
		<InspirationSection title={t(R.comparisonTitle)} gap={1.2}>
			<Stack gap={0.4}>
				<Typography variant="body2" lineHeight={1.625}>
					<Trans
						i18nKey={getResourcePath(
							savings > 0 ? R.comparisonTextWithSavings : R.comparisonText,
						)}
						values={{
							rent: formatCurrency(rent, INSPIRATION_CURRENCY, 0),
							savings: formatCurrency(savings, INSPIRATION_CURRENCY, 0),
							range: formatMillionRange(priceLow, priceHigh),
						}}
						components={{
							b: <Typography component="span" variant="bodyStrong" tabular />,
							blue: (
								<Typography
									component="span"
									variant="bodyStrong"
									color={theme.palette.blue.main}
									tabular
								/>
							),
						}}
					/>
				</Typography>

				<Typography variant="hint" color={theme.palette.grey[500]}>
					{t(R.comparisonSourceNote)}
				</Typography>
			</Stack>

			<Stack direction="row" gap={1.2}>
				<ComparisonColumn
					bg={theme.palette.surface.muted}
					iconBg={theme.palette.grey[200]}
					icon={
						<Banknote
							size={theme.iconSize.sm}
							color={theme.palette.grey[500]}
						/>
					}
					tag={t(R.comparisonRentTag)}
					tagColor={theme.palette.grey[500]}
					totalValue={formatCurrency(totalRent, INSPIRATION_CURRENCY, 0)}
					dividerColor={theme.palette.grey[200]}
					endLabel={t(R.comparisonEndLabel)}
					endValue={
						<Typography
							variant="hint"
							fontWeight={600}
							color={theme.palette.red.main}
						>
							{t(R.comparisonRentEnd)}
						</Typography>
					}
				/>
				<ComparisonColumn
					bg={theme.palette.blue[50]}
					iconBg={theme.palette.blue[100]}
					icon={
						<Home size={theme.iconSize.sm} color={theme.palette.blue.main} />
					}
					tag={t(R.comparisonOwnTag)}
					tagColor={theme.palette.blue.main}
					totalValue={`~${formatCurrency(totalRent, INSPIRATION_CURRENCY, 0)}`}
					dividerColor={theme.palette.blue[100]}
					endLabel={t(R.comparisonPropertyLabel, {
						years: OWN_HOME_HORIZON_YEARS,
					})}
					endValue={
						<Typography
							variant="hint"
							fontWeight={600}
							color={theme.palette.blue.main}
							tabular
						>
							{formatMillionRange(propertyLow, propertyHigh)}
						</Typography>
					}
				/>
			</Stack>

			<Typography
				variant="hint"
				color={theme.palette.grey[500]}
				lineHeight={1.625}
			>
				{t(R.comparisonHint)}
				{state === "affordability" && t(R.comparisonHintExpenses)}
			</Typography>
		</InspirationSection>
	);
};
